
import { useState, useRef, useEffect } from "react";
import { Send, Loader2, PenSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscriptionManager } from "@/hooks/useSubscriptionManager";
import { PLAN_LIMITS } from "@/hooks/useSubscriptionManager";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useUserPlan } from "@/hooks/useUserPlan";

interface ChatInputAreaProps {
  handleSendMessage: (message: string) => void;
  isLoading: boolean;
}

export const ChatInputArea = ({
  handleSendMessage,
  isLoading,
}: ChatInputAreaProps) => {
  const [message, setMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { planType, reportsRemaining, nextResetDays, isLoading: isPlanLoading } = useUserPlan();
  
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!message.trim()) {
      toast("Please enter a message");
      return;
    }

    if (isLoading) {
      return;
    }
    
    if (planType === 'free' && (reportsRemaining === 0 || reportsRemaining === null)) {
      toast("You've used your 2 free reports. Please upgrade to continue.", { 
        description: "Upgrade to unlock more reports."
      });
      handleSendMessage("__SHOW_PRICING_SHEET__");
      setMessage("");
      return;
    }
    
    if (planType === 'premium' && reportsRemaining === 0) {
      toast("You've reached your Premium plan limit. Please contact support@analystx.co for additional reporting needs and custom pricing.");
      return;
    }
    
    if (planType === 'starter' && reportsRemaining === 0) {
      toast("You've reached your Starter plan limit of 10 reports. Please upgrade to continue.");
      handleSendMessage("__SHOW_PRICING_SHEET__");
      setMessage("");
      return;
    }
    
    if (planType === 'pro' && reportsRemaining === 0) {
      toast("You've reached your Pro plan limit of 30 reports. Please upgrade to continue.");
      handleSendMessage("__SHOW_PRICING_SHEET__");
      setMessage("");
      return;
    }

    handleSendMessage(message);
    setMessage("");

    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleNewAnalysis = () => {
    // Clear the local chat state
    localStorage.removeItem('analystx_chat_state');
    
    // Instead of navigating away, stay in the current route but add a timestamp parameter
    // to force a refresh of the chat component
    navigate(`?new=${Date.now()}`, { replace: true });
    
    // Force a refresh of the current component without a full page reload
    window.location.reload();
  };

  // Example prompts based on user's plan type
  const getPlaceholderText = () => {
    if (isPlanLoading) return "Loading...";
    
    if (reportsRemaining === 0) {
      return "You've reached your report limit. Please upgrade to continue.";
    }
    
    return "Analyze the rental market trends in San Francisco over the past 5 years...";
  };

  return (
    <div className="fixed bottom-6 left-0 right-0 z-10">
      <div className="max-w-3xl mx-auto px-4">
        <form onSubmit={handleSubmit} className="relative">
          <div className="flex flex-col gap-1 bg-black/80 backdrop-blur-sm p-3 rounded-2xl shadow-lg border border-gray-800">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-10 w-10 rounded-full border-gray-600 text-gray-400 hover:text-white hover:bg-gray-800"
                onClick={handleNewAnalysis}
                title="New Report"
                aria-label="Start a new report"
              >
                <PenSquare className="h-5 w-5" />
                <span className="sr-only">New Report</span>
              </Button>

              <div className="relative flex-1">
                <Textarea
                  ref={textareaRef}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={getPlaceholderText()}
                  className="min-h-12 resize-none py-3 pr-12 bg-gray-900 border-gray-700 text-white placeholder:text-gray-400 focus-visible:ring-blue-600 rounded-xl"
                  disabled={isLoading || (reportsRemaining !== null && reportsRemaining <= 0 && planType === 'free')}
                />
                <Button
                  type="submit"
                  size="icon"
                  disabled={isLoading || !message.trim() || (reportsRemaining !== null && reportsRemaining <= 0 && planType === 'free')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-700"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  <span className="sr-only">Send</span>
                </Button>
              </div>
            </div>
            
            {/* Reports remaining indicator with reset information */}
            <div className="text-xs text-gray-400 italic text-right px-2 flex justify-end items-center gap-2">
              {!isPlanLoading && (
                <span>
                  {reportsRemaining !== null 
                    ? `${reportsRemaining} report${reportsRemaining !== 1 ? 's' : ''} remaining`
                    : planType === 'free' 
                      ? '2 reports remaining' 
                      : ''}
                  {nextResetDays !== null && nextResetDays > 0 && planType !== 'admin' ? 
                    ` • Resets in ${nextResetDays} day${nextResetDays !== 1 ? 's' : ''}` : ''}
                </span>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
